import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Pin, X, Loader2, ShieldAlert } from 'lucide-react';

const SEV = {
  info: { bg: 'rgba(26,76,124,0.07)', border: 'rgba(26,76,124,0.2)', color: '#1A4C7C', label: 'INFO' },
  caution: { bg: 'rgba(232,160,32,0.08)', border: 'rgba(232,160,32,0.3)', color: '#B27516', label: 'CAUTION' },
  critical: { bg: 'rgba(226,75,74,0.07)', border: 'rgba(226,75,74,0.3)', color: '#E24B4A', label: 'CRITICAL' },
};

export const ClientRiskNotes = ({ entityId, editable = false, compact = false }) => {
  const api = useApi();
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState('');
  const [severity, setSeverity] = useState('caution');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    api.get(`/entities/${entityId}/notes`)
      .then(r => setNotes(r.data.notes))
      .catch(err => { console.error('API error:', err?.message); setNotes([]); });
  }, [api, entityId]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!text.trim()) return;
    setSaving(true); setError('');
    try {
      await api.post(`/entities/${entityId}/notes`, { text, severity });
      setText(''); load();
    } catch (err) { setError(err?.response?.data?.detail || 'Failed to pin note'); }
    setSaving(false);
  };

  const removeNote = async (noteId) => {
    try { await api.delete(`/entities/${entityId}/notes/${noteId}`); load(); }
    catch (err) { setError(err?.response?.data?.detail || 'Failed to remove note'); }
  };

  if (notes === null) return compact ? null : <div className="flex items-center gap-2 py-2 text-xs" style={{ color: '#5C6B7A' }}><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading risk notes...</div>;
  if (compact && notes.length === 0) return null;

  return (
    <div data-testid="client-risk-notes">
      {!compact && (
        <div className="flex items-center gap-1.5 mb-2">
          <ShieldAlert className="w-3.5 h-3.5" style={{ color: '#B27516' }} />
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Client Risk Notes</span>
          <span className="text-[10px]" style={{ color: '#64748B' }}>— surface on every future submission for this client</span>
        </div>
      )}
      {notes.length === 0 && !compact && <p className="text-xs py-1" style={{ color: '#5C6B7A' }}>No pinned notes for this client.</p>}
      <div className="space-y-1.5">
        {notes.map(n => {
          const s = SEV[n.severity] || SEV.caution;
          return (
            <div key={n.note_id} className="flex items-start gap-2 px-2.5 py-2 rounded-md" data-testid={`risk-note-${n.note_id}`}
              style={{ background: s.bg, border: `1px solid ${s.border}` }}>
              <Pin className="w-3 h-3 mt-0.5 shrink-0" style={{ color: s.color }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="px-1.5 py-px rounded text-[8px] font-bold tracking-wider" style={{ background: '#FFFFFF', color: s.color }}>{s.label}</span>
                  <span className="text-[10px]" style={{ color: '#5C6B7A' }}>{n.created_by} · {new Date(n.created_at).toLocaleDateString('en-AU')}</span>
                </div>
                <p className="text-xs mt-0.5 leading-relaxed" style={{ color: '#0D1B2A' }}>{n.text}</p>
              </div>
              {editable && (
                <button onClick={() => removeNote(n.note_id)} title="Remove note" className="p-0.5 rounded hover:bg-white/60 transition-colors" data-testid={`risk-note-delete-${n.note_id}`}>
                  <X className="w-3 h-3" style={{ color: '#5C6B7A' }} />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {editable && (
        <div className="mt-2.5">
          <div className="flex items-start gap-2">
            <textarea value={text} onChange={e => setText(e.target.value)} rows={2}
              placeholder="Pin a risk note for this client (visible to AI and underwriters on every future submission)..."
              className="flex-1 px-2.5 py-1.5 text-xs rounded-md border resize-none focus:outline-none"
              style={{ borderColor: 'rgba(136,150,168,0.25)', color: '#0D1B2A' }}
              data-testid="risk-note-input" />
            <div className="flex flex-col gap-1.5">
              <select value={severity} onChange={e => setSeverity(e.target.value)}
                className="px-2 py-1 text-[10px] rounded border" style={{ borderColor: 'rgba(136,150,168,0.25)', color: '#5C6B7A' }}
                data-testid="risk-note-severity">
                <option value="info">Info</option>
                <option value="caution">Caution</option>
                <option value="critical">Critical</option>
              </select>
              <button onClick={addNote} disabled={saving || !text.trim()}
                className="flex items-center justify-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
                style={{ background: '#1A4C7C' }} data-testid="risk-note-pin-btn">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Pin className="w-3 h-3" />}Pin Note
              </button>
            </div>
          </div>
          {error && <p className="text-[10px] mt-1" style={{ color: '#E24B4A' }} data-testid="risk-note-error">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default ClientRiskNotes;
